import {FC, useEffect, useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import {useAuth} from '../../auth'
import {useGlobalFilter} from '../../../shared/context/GlobalFilterContext'
import {useProcurementPermissions} from '../core/permissionAccess'
import {
  createApprovalLimit,
  deleteApprovalLimit,
  getApprovalLimits,
  getCompanyGroupAdmins,
  setApprovalLimitActive,
  updateApprovalLimit,
} from '../core/_requests'
import {ApprovalLimit, ApprovalPolicyModuleType} from '../core/_models'
import {PROCUREMENT_PERMS} from '../core/permissions'
import {getAssignableRolesForUserCreate, getCurrencies, getManagementUsers} from '../../Management/core/_requests'
import type {AssignableRoleForUserCreate, ManagementUser} from '../../Management/core/_models'
import type {CurrencyOption} from '../../Management/core/_requests'
import CurrencySelect from '../../Management/components/CurrencySelect'

type FormState = {
  id?: number | null
  companyGroupAdminId: number | null
  moduleType: ApprovalPolicyModuleType | ''
  subjectType: 'ROLE' | 'USER'
  roleId: number | null
  userId: number | null
  currency: string
  approvalLimit: string
  active: boolean
}

const defaultForm: FormState = {
  id: null,
  companyGroupAdminId: null,
  moduleType: '',
  subjectType: 'ROLE',
  roleId: null,
  userId: null,
  currency: 'USD',
  approvalLimit: '',
  active: true,
}

const modules: ApprovalPolicyModuleType[] = ['PURCHASE_ORDER', 'INVOICE']

const normalizeRoleRows = (response: unknown): AssignableRoleForUserCreate[] => {
  const raw =
    (response as {data?: unknown})?.data ??
    (response as {content?: unknown})?.content ??
    (response as {items?: unknown})?.items ??
    response ??
    []

  const rows = Array.isArray(raw)
    ? raw
    : Array.isArray((raw as {content?: unknown})?.content)
      ? (raw as {content: unknown[]}).content
      : Array.isArray((raw as {items?: unknown})?.items)
        ? (raw as {items: unknown[]}).items
        : Array.isArray((raw as {data?: unknown})?.data)
          ? (raw as {data: unknown[]}).data
          : []

  return rows.filter((row): row is AssignableRoleForUserCreate => !!row && typeof row === 'object')
}

const ApprovalLimitsPanel: FC = () => {
  const {currentUser} = useAuth()
  const {can} = useProcurementPermissions()
  const {state: globalState} = useGlobalFilter()
  const canRead = can(PROCUREMENT_PERMS.READ_APPROVAL_LIMIT) || String((currentUser as any)?.role?.roleType || '').toUpperCase() === 'SUPER_ADMIN'
  const canCreate = can(PROCUREMENT_PERMS.CREATE_APPROVAL_LIMIT)
  const canUpdate = can(PROCUREMENT_PERMS.UPDATE_APPROVAL_LIMIT)
  const canDelete = can(PROCUREMENT_PERMS.DELETE_APPROVAL_LIMIT)

  const [rows, setRows] = useState<ApprovalLimit[]>([])
  const [companyGroups, setCompanyGroups] = useState<any[]>([])
  const [roles, setRoles] = useState<AssignableRoleForUserCreate[]>([])
  const [users, setUsers] = useState<ManagementUser[]>([])
  const [currencies, setCurrencies] = useState<CurrencyOption[]>([])
  const [loading, setLoading] = useState(false)
  const [rolesLoading, setRolesLoading] = useState(false)
  const [rolesError, setRolesError] = useState('')
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState('')
  const [moduleFilter, setModuleFilter] = useState<'' | ApprovalPolicyModuleType>('')
  const [companyFilter, setCompanyFilter] = useState<number | ''>('')
  const [form, setForm] = useState<FormState>(defaultForm)

  // Sync global header company filter → local companyFilter
  useEffect(() => {
    if (globalState.company) {
      setCompanyFilter(globalState.company.id)
    } else {
      setCompanyFilter('')
    }
  }, [globalState.company])

  const loadLists = async () => {
    try {
      const [groups, roleRows, userRows, currencyRows] = await Promise.all([
        getCompanyGroupAdmins().catch(() => []),
        getAssignableRolesForUserCreate(true, form.companyGroupAdminId ?? undefined).catch(() => []),
        getManagementUsers().catch(() => []),
        getCurrencies().catch(() => []),
      ])
      setCompanyGroups(groups)
      setRoles(roleRows.filter((r) => r.active !== false))
      setUsers(userRows.filter((u) => u.enabled !== false))
      setCurrencies(currencyRows.filter((currency) => currency.active !== false))
    } catch {
      setCompanyGroups([])
      setRoles([])
      setUsers([])
    }
  }

  const loadRoles = async (companyGroupAdminId: number | null, subjectType: FormState['subjectType']) => {
    if (subjectType !== 'ROLE') {
      setRoles([])
      setRolesError('')
      setRolesLoading(false)
      return
    }
    setRolesLoading(true)
    setRolesError('')
    try {
      const response = await getAssignableRolesForUserCreate(true, companyGroupAdminId ?? undefined)
      const normalized = normalizeRoleRows(response).filter((role) => role.active !== false)
      setRoles(normalized)
    } catch (error: any) {
      setRoles([])
      setRolesError(error?.message || 'Failed to load roles')
      toast.error(error?.message || 'Failed to load roles')
    } finally {
      setRolesLoading(false)
    }
  }

  const loadLimits = async () => {
    if (!canRead) return
    setLoading(true)
    try {
      const data = await getApprovalLimits({
        companyGroupAdminId: companyFilter || undefined,
        moduleType: moduleFilter || undefined,
        search: search || undefined,
      })
      setRows(data)
    } catch (error: any) {
      toast.error(error.message || 'Failed to load approval limits')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadLists()
  }, [form.companyGroupAdminId])

  useEffect(() => {
    void loadLimits()
  }, [search, moduleFilter, companyFilter, canRead])

  useEffect(() => {
    void loadRoles(form.companyGroupAdminId, form.subjectType)
  }, [form.companyGroupAdminId, form.subjectType])

  useEffect(() => {
    if (form.subjectType !== 'ROLE') return
    if (!form.roleId) return
    if (!roles.some((role) => Number(role.id) === Number(form.roleId))) {
      setForm((prev) => ({...prev, roleId: null}))
    }
  }, [form.subjectType, form.roleId, roles])

  const filteredUsers = useMemo(() => users, [users])
  const historicalOwnerApproval = form.moduleType === 'QUOTATION_OWNER_APPROVAL'
  const currencyOptions = useMemo(() => {
    if (!form.currency || currencies.some((currency) => currency.code === form.currency)) return currencies
    return [
      ...currencies,
      {
        code: form.currency,
        displayName: `${form.currency} (legacy)`,
        decimalPlaces: 2,
        active: false,
      },
    ]
  }, [currencies, form.currency])

  const handleCompanyChange = (companyGroupAdminId: number | null) => {
    setForm((prev) => ({
      ...prev,
      companyGroupAdminId,
      roleId: null,
      userId: null,
    }))
  }

  const handleSubjectTypeChange = (subjectType: FormState['subjectType']) => {
    setForm((prev) => ({
      ...prev,
      subjectType,
      roleId: subjectType === 'ROLE' ? prev.roleId : null,
      userId: subjectType === 'USER' ? prev.userId : null,
    }))
  }

  const save = async () => {
    if (!form.companyGroupAdminId || !form.moduleType || !form.currency || !form.approvalLimit) {
      toast.error('Fill all required fields')
      return
    }
    setSaving(true)
    try {
      const payload = {
        companyGroupAdminId: form.companyGroupAdminId,
        moduleType: form.moduleType,
        subjectType: form.subjectType,
        roleId: form.subjectType === 'ROLE' ? form.roleId : null,
        userId: form.subjectType === 'USER' ? form.userId : null,
        currency: form.currency,
        approvalLimit: Number(form.approvalLimit),
        active: form.active,
      }
      if (form.id) {
        await updateApprovalLimit(form.id, payload)
      } else {
        await createApprovalLimit(payload as any)
      }
      toast.success('Approval limit saved')
      setForm(defaultForm)
      await loadLimits()
    } catch (error: any) {
      toast.error(error.message || 'Failed to save approval limit')
    } finally {
      setSaving(false)
    }
  }

  const edit = (row: ApprovalLimit) => {
    setForm({
      id: row.id,
      companyGroupAdminId: row.companyGroupAdminId,
      moduleType: row.moduleType,
      subjectType: row.subjectType,
      roleId: row.roleId ?? null,
      userId: row.userId ?? null,
      currency: row.currency,
      approvalLimit: String(row.approvalLimit),
      active: row.active,
    })
  }

  if (!canRead) {
    return <div className='alert alert-warning m-6'>You do not have access to approval limits.</div>
  }

  return (
    <div className='container-fluid py-4'>
      <div className='card shadow-sm mb-4'>
        <div className='card-body'>
          <div className='d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3'>
            <div>
              <h3 className='h5 mb-1'>Approval Limits</h3>
              <div className='text-muted small'>Super Admin and Company Group Admin have unlimited approval authority within their accessible scope.</div>
            </div>
            <button className='btn btn-sm btn-outline-secondary' onClick={() => void loadLimits()}>
              Refresh
            </button>
          </div>

          <div className='row g-3 mb-4'>
            <div className='col-md-3'>
              <label className='form-label'>Module</label>
              <select className='form-select' value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value as any)}>
                <option value=''>All</option>
                {modules.map((moduleType) => (
                  <option key={moduleType} value={moduleType}>{moduleType.replaceAll('_', ' ')}</option>
                ))}
              </select>
            </div>
            <div className='col-md-3'>
              <label className='form-label'>Search</label>
              <input className='form-control' value={search} onChange={(e) => setSearch(e.target.value)} placeholder='Company / role / user / currency' />
            </div>
          </div>

          <div className='table-responsive mb-4'>
            <table className='table table-row-dashed align-middle gs-0 gy-3'>
              <thead>
                <tr className='text-muted fw-semibold fs-7 text-uppercase'>
                  <th>Company</th>
                  <th>Module</th>
                  <th>Subject</th>
                  <th>Currency</th>
                  <th>Limit</th>
                  <th>Active</th>
                  <th className='text-end'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className='text-center text-muted py-5'>Loading...</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={7} className='text-center text-muted py-5'>No approval limits found.</td></tr>
                ) : rows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.companyGroupAdminName || row.companyGroupAdminId}</td>
                    <td>{row.moduleType.replaceAll('_', ' ')}</td>
                    <td>{row.subjectType} {row.subjectType === 'ROLE' ? row.roleName || row.roleId : row.userName || row.userId}</td>
                    <td>{row.currency}</td>
                    <td>{Number(row.approvalLimit).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                    <td>{row.active ? 'Yes' : 'No'}</td>
                    <td className='text-end'>
                      <button className='btn btn-sm btn-light-primary me-2' onClick={() => edit(row)}>Edit</button>
                      <button className='btn btn-sm btn-light-danger me-2' disabled={!canDelete} onClick={async () => {
                        if (!window.confirm('Delete this approval limit?')) return
                        await deleteApprovalLimit(row.id)
                        await loadLimits()
                      }}>Delete</button>
                      <button className='btn btn-sm btn-light-secondary' onClick={async () => {
                        await setApprovalLimitActive(row.id, !row.active)
                        await loadLimits()
                      }}>{row.active ? 'Deactivate' : 'Activate'}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className='border rounded p-4 bg-light'>
            <h4 className='h6 mb-3'>{form.id ? 'Edit approval limit' : 'Add approval limit'}</h4>
            <div className='row g-3'>
              <div className='col-md-3'>
                <label className='form-label'>Company</label>
                <select className='form-select' value={form.companyGroupAdminId ?? ''} onChange={(e) => handleCompanyChange(e.target.value ? Number(e.target.value) : null)}>
                  <option value=''>Select company</option>
                  {companyGroups.map((cg) => (
                    <option key={cg.id} value={cg.id}>{cg.name || cg.username || cg.id}</option>
                  ))}
                </select>
              </div>
              <div className='col-md-3'>
                <label className='form-label'>Module</label>
                {historicalOwnerApproval ? (
                  <input className='form-control' value='QUOTATION OWNER APPROVAL' disabled />
                ) : (
                  <select className='form-select' value={form.moduleType} onChange={(e) => setForm((prev) => ({...prev, moduleType: e.target.value as any}))}>
                    <option value=''>Select module</option>
                    {modules.map((moduleType) => (
                      <option key={moduleType} value={moduleType}>{moduleType.replaceAll('_', ' ')}</option>
                    ))}
                  </select>
                )}
              </div>
              <div className='col-md-3'>
                <label className='form-label'>Assignment Type</label>
                <select className='form-select' value={form.subjectType} onChange={(e) => handleSubjectTypeChange(e.target.value as any)}>
                  <option value='ROLE'>ROLE</option>
                  <option value='USER'>USER</option>
                </select>
              </div>
              <div className='col-md-3'>
                <label className='form-label'>Currency</label>
                <CurrencySelect
                  ariaLabel='Approval limit currency'
                  className='form-select'
                  currencies={currencyOptions}
                  value={form.currency}
                  onChange={(value) => setForm((prev) => ({...prev, currency: value}))}
                  required
                />
              </div>
              {form.subjectType === 'ROLE' ? (
                <div className='col-md-3'>
                  <label className='form-label'>Role</label>
                  <select className='form-select' value={form.roleId ?? ''} onChange={(e) => setForm((prev) => ({...prev, roleId: e.target.value ? Number(e.target.value) : null}))}>
                    <option value=''>Select role</option>
                    {rolesLoading ? (
                      <option value='' disabled>Loading roles...</option>
                    ) : (
                      roles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.displayName || role.code || role.roleType}
                        </option>
                      ))
                    )}
                  </select>
                  {rolesError ? <div className='text-danger small mt-1'>{rolesError}</div> : null}
                </div>
              ) : (
                <div className='col-md-3'>
                  <label className='form-label'>User</label>
                  <select className='form-select' value={form.userId ?? ''} onChange={(e) => setForm((prev) => ({...prev, userId: e.target.value ? Number(e.target.value) : null}))}>
                    <option value=''>Select user</option>
                    {filteredUsers.map((user) => (
                      <option key={user.userId} value={user.userId}>{user.username}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className='col-md-3'>
                <label className='form-label'>Approval Limit</label>
                <input className='form-control' type='number' min='0' step='0.01' value={form.approvalLimit} onChange={(e) => setForm((prev) => ({...prev, approvalLimit: e.target.value}))} />
              </div>
              <div className='col-md-3 d-flex align-items-end'>
                <div className='form-check'>
                  <input className='form-check-input' type='checkbox' checked={form.active} onChange={(e) => setForm((prev) => ({...prev, active: e.target.checked}))} />
                  <label className='form-check-label'>Active</label>
                </div>
              </div>
            </div>
            <div className='d-flex gap-2 mt-4'>
              <button className='btn btn-primary' disabled={saving || (!canCreate && !form.id)} onClick={() => void save()}>
                {form.id ? 'Update' : 'Create'}
              </button>
              <button className='btn btn-light' onClick={() => setForm(defaultForm)}>Reset</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ApprovalLimitsPanel
